import { useState, useEffect } from "react";
import TenantLayout from "@/pages/tenant/components/TenantLayout";
import { 
  getTenantUsers, 
  createTenantUser, 
  updateTenantUser, 
  deleteTenantUser,
  getTenantAssignableSites,
  type TenantUser 
} from "@/utils/tenantApi";

const statusLabels: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: "正常", color: "text-green-600", bg: "bg-green-50" },
  suspended: { label: "已停用", color: "text-red-600", bg: "bg-red-50" },
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function TenantUsersPage() {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await getTenantUsers({
        search: searchQuery || undefined,
        limit: 100,
      });
      setUsers(res.list);
    } catch (err: any) {
      console.error('获取用户列表失败:', err);
      showToast(err.message || "获取数据失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery]);

  const handleCreate = async (data: any) => {
    try {
      await createTenantUser(data);
      showToast("用户创建成功");
      setShowModal(false);
      fetchUsers();
    } catch (err: any) {
      showToast(err.message || "创建失败");
    }
  };

  const handleUpdate = async (id: string, data: any) => {
    try {
      await updateTenantUser(id, data);
      showToast("用户信息已更新");
      setShowModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      showToast(err.message || "更新失败");
    }
  };

  const handleToggleStatus = async (user: TenantUser) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      await updateTenantUser(user.id, { status: newStatus });
      showToast(newStatus === 'active' ? "用户已启用" : "用户已停用");
      fetchUsers();
    } catch (err: any) {
      showToast(err.message || "操作失败");
    }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm("确定要删除此用户吗？")) return;
    try {
      await deleteTenantUser(userId);
      showToast("用户已删除");
      fetchUsers();
    } catch (err: any) {
      showToast(err.message || "删除失败");
    }
  };

  const filteredUsers = users.filter(user => {
    if (activeTab !== 'all' && user.status !== activeTab) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return user.username.toLowerCase().includes(q) || 
             user.nickname?.toLowerCase().includes(q) ||
             user.phone.includes(q);
    }
    return true;
  });

  const statusCounts = {
    all: users.length,
    active: users.filter(u => u.status === 'active').length,
    suspended: users.filter(u => u.status === 'suspended').length,
  };

  return (
    <TenantLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索用户名或手机号..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => {
              setEditingUser(null);
              setShowModal(true);
            }}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            创建用户
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full mb-6 w-fit">
          <button
            onClick={() => setActiveTab("all")}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === "all" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            全部 ({statusCounts.all})
          </button>
          <button
            onClick={() => setActiveTab("active")}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === "active" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            正常 ({statusCounts.active})
          </button>
          <button
            onClick={() => setActiveTab("suspended")}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === "suspended" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            已停用 ({statusCounts.suspended})
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-gray-500">用户名</th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-gray-500">手机号</th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-gray-500">站点数量</th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-gray-500">状态</th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-gray-500">创建时间</th>
                  <th className="text-right px-5 py-3.5 text-xs font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-gray-400">
                      <i className="ri-loader-4-line animate-spin text-2xl" />
                    </td>
                  </tr>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const status = statusLabels[user.status] || statusLabels.active;
                    return (
                      <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-50 text-orange-500 text-xs font-bold">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <span className="font-medium text-gray-800">{user.nickname || user.username}</span>
                              <span className="text-gray-400 text-xs ml-1">@{user.username}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{user.phone || '-'}</td>
                        <td className="px-5 py-4">
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-600">
                            {user.site_count} 个
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-500">{formatDate(user.created_at)}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => {
                                setEditingUser(user);
                                setShowModal(true);
                              }}
                              className="px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user)}
                              className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                                user.status === 'active'
                                  ? "text-red-500 hover:bg-red-50"
                                  : "text-green-500 hover:bg-green-50"
                              }`}
                            >
                              {user.status === 'active' ? '停用' : '启用'}
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-gray-400">
                      <i className="ri-user-search-line text-3xl mb-2 block" />
                      <p className="text-sm">未找到匹配的用户</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <UserModal
            user={editingUser}
            onClose={() => {
              setShowModal(false);
              setEditingUser(null);
            }}
            onSave={editingUser ? (data) => handleUpdate(editingUser.id, data) : handleCreate}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
            <i className="ri-checkbox-circle-line text-green-400" />
            {toast}
          </div>
        )}
      </div>
    </TenantLayout>
  );
}

/* ── User Modal ── */
function UserModal({
  user,
  onClose,
  onSave,
}: {
  user?: TenantUser | null;
  onClose: () => void;
  onSave: (data: any) => void;
}) {
  const [username, setUsername] = useState(user?.username || "");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState(user?.nickname || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!username.trim()) { setError("请输入账号"); return; }
    if (!user && !password.trim()) { setError("请输入密码"); return; }
    if (!phone.trim()) { setError("请输入手机号"); return; }

    setSubmitting(true);
    try {
      const data: any = {
        username: username.trim(),
        phone: phone.trim(),
      };
      if (nickname.trim()) data.nickname = nickname.trim();
      if (password.trim()) data.password = password.trim();
      await onSave(data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">{user ? "编辑用户" : "创建用户"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">账号</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="登录账号"
              disabled={!!user}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:text-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              密码 {!user && <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={user ? "留空则不修改密码" : "设置登录密码"}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            {user && <p className="text-xs text-gray-400 mt-1">留空则不修改密码</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">昵称</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="用户昵称"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">手机号</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="手机号码"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '处理中...' : (user ? '保存' : '创建')}
          </button>
        </div>
      </div>
    </div>
  );
}
